(() => {
  if (window.CleoColorwayGridBackfill) return;
  window.CleoColorwayGridBackfill = true;

  const controllerSelector = '[data-colorway-load-more]';

  // Pages already requested (url -> Promise<html>), so a batch fetched in the
  // background is ready the moment the shopper clicks "Load more".
  const pageCache = new Map();

  function getGrid(controller) {
    return document.getElementById(controller.dataset.containerId);
  }

  function getCards(grid) {
    return Array.from(grid.querySelectorAll(':scope > .shop__grid-item'));
  }

  function getSectionId(controller) {
    const section = controller.closest('.shopify-section');
    return section && section.id ? section.id.replace(/^shopify-section-/, '') : '';
  }

  function updateController(controller) {
    const grid = getGrid(controller);
    if (!grid) return;

    const count = getCards(grid).length;
    const countNode = controller.querySelector('[data-colorway-loaded-count]');
    const button = controller.querySelector('[data-colorway-load-button]');

    if (countNode) countNode.textContent = count + (count === 1 ? ' style shown' : ' styles shown');
    if (button) button.hidden = !controller.dataset.colorwayNextUrl;
  }

  function requestUrl(path, sectionId) {
    const url = new URL(path, window.location.origin);
    const current = new URL(window.location.href);

    ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
      if (current.searchParams.has(key) && !url.searchParams.has(key)) {
        url.searchParams.set(key, current.searchParams.get(key));
      }
    });

    // Only render the product grid section, not the whole page (much smaller download).
    if (sectionId) url.searchParams.set('section_id', sectionId);

    return url.toString();
  }

  function fetchPage(controller, path) {
    const url = requestUrl(path, getSectionId(controller));
    if (!pageCache.has(url)) {
      const request = fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }).then((response) => {
        if (!response.ok) throw new Error('Unable to load the next collection batch.');
        return response.text();
      });
      request.catch(() => pageCache.delete(url));
      pageCache.set(url, request);
    }
    return pageCache.get(url);
  }

  function prefetchNext(controller) {
    const next = controller.dataset.colorwayNextUrl;
    if (next) fetchPage(controller, next).catch(() => {});
  }

  async function fillToTarget(controller, targetCount) {
    if (controller.dataset.colorwayLoading === 'true') {
      controller.dataset.colorwayPendingTarget = String(
        Math.max(Number(controller.dataset.colorwayPendingTarget || 0), targetCount),
      );
      return;
    }

    controller.dataset.colorwayLoading = 'true';
    const button = controller.querySelector('[data-colorway-load-button]');
    const label = controller.querySelector('[data-colorway-button-label]');
    if (label) label.textContent = 'Loading…';

    try {
      let grid = getGrid(controller);

      while (
        controller.isConnected &&
        grid &&
        getCards(grid).length < targetCount &&
        controller.dataset.colorwayNextUrl
      ) {
        const html = await fetchPage(controller, controller.dataset.colorwayNextUrl);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incomingGrid = doc.getElementById(controller.dataset.containerId);
        const incomingController = doc.querySelector(controllerSelector);

        if (!incomingGrid) {
          controller.removeAttribute('data-colorway-next-url');
          break;
        }

        const existingFamilies = new Set(
          getCards(grid).map((card) => card.dataset.colorwayFamily || ''),
        );

        getCards(incomingGrid).forEach((card) => {
          const family = card.dataset.colorwayFamily || '';
          if (family && existingFamilies.has(family)) return;
          if (family) existingFamilies.add(family);
          grid.appendChild(document.importNode(card, true));
        });

        const nextUrl = incomingController?.dataset.colorwayNextUrl;
        if (nextUrl) {
          controller.dataset.colorwayNextUrl = nextUrl;
        } else {
          controller.removeAttribute('data-colorway-next-url');
        }

        updateController(controller);
      }
    } catch (error) {
      console.error('[Colorway grid]', error);
    } finally {
      controller.dataset.colorwayLoading = 'false';
      if (button) button.disabled = false;
      if (label) label.textContent = 'Load more';
      updateController(controller);

      const pendingTarget = Number(controller.dataset.colorwayPendingTarget || 0);
      controller.removeAttribute('data-colorway-pending-target');
      const grid = getGrid(controller);
      if (pendingTarget && grid && getCards(grid).length < pendingTarget) {
        fillToTarget(controller, pendingTarget);
      } else {
        // Get the next batch ready in the background for the next click.
        prefetchNext(controller);
      }
    }
  }

  function initialize(controller) {
    if (controller.dataset.colorwayInitialized === 'true') return;
    controller.dataset.colorwayInitialized = 'true';

    const grid = getGrid(controller);
    if (!grid) return;

    const pageSize = Math.max(1, Number(controller.dataset.pageSize || 20));
    updateController(controller);
    fillToTarget(controller, pageSize);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-colorway-load-button]');
    if (!button) return;

    const controller = button.closest(controllerSelector);
    const grid = controller && getGrid(controller);
    if (!controller || !grid) return;

    event.preventDefault();
    const pageSize = Math.max(1, Number(controller.dataset.pageSize || 20));
    // Each click asks for one more batch on top of whatever is already shown or on its way.
    const base = Math.max(getCards(grid).length, Number(controller.dataset.colorwayPendingTarget || 0));
    fillToTarget(controller, base + pageSize);
  });

  function initializeAll(root = document) {
    if (root.matches?.(controllerSelector)) initialize(root);
    root.querySelectorAll?.(controllerSelector).forEach(initialize);
  }

  // Speed (Oct 2026): the grid top-up waits until the page has finished loading and the browser is idle,
  // so it never competes with the first photos or causes freezes while the page is still coming in.
  // The top-up adds cards at the end of the grid, below what's on screen at first.
  function start() {
    initializeAll();

    // Grids added later (e.g. filters or sorting re-render the section).
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.firstElementChild) initializeAll(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2500 });
    } else {
      setTimeout(callback, 300);
    }
  }

  if (document.readyState === 'complete') {
    whenIdle(start);
  } else {
    window.addEventListener('load', () => whenIdle(start), { once: true });
  }
})();
